import axios from 'axios';
import constant from "../constants/constant";
console.log("Constant", constant);
const api = axios.create({
  baseURL: constant.API_BASE_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('adminToken') || sessionStorage.getItem('agencyToken') || sessionStorage.getItem('sellerToken');
    if (token && token !== 'true') {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('adminToken');
      sessionStorage.removeItem('agencyToken');
      sessionStorage.removeItem('sellerToken');
      const isAgency = window.location.pathname.startsWith('/agency');
      const isSeller = window.location.pathname.startsWith('/seller');
      window.location.href = isSeller ? '/seller/login' : (isAgency ? '/agency/login' : '/login');
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getProfile: () => api.get('/auth/getuser'),
  getRoles: () => api.get('/auth/roles'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const usersAPI = {
  getUsers: (params) => api.get('/auth/users', { params }),
  getUser: (id) => api.get(`/auth/users/${id}`),
  createUser: (data) => api.post('/auth/create-user', data),
  updateUser: (id, data) => api.put(`/auth/${id}`, data),
  deleteUser: (id) => api.delete(`/auth/${id}`),
};

export const sessionsAPI = {
  getActiveSessions: () => api.get('/admin/sessions/active'),
  getSessions: (params) => api.get('/admin/sessions', { params }),
  getSession: (id) => api.get(`/admin/sessions/${id}`),
  endSession: (id) => api.put(`/admin/sessions/${id}/end`),
  getSessionStats: () => api.get('/admin/sessions/stats'),
};

export const giftsAPI = {
  getGifts: (params) => api.get('/admin/gifts', { params }),
  getActiveGifts: () => api.get('/admin/gifts/active'),
  getGift: (id) => api.get(`/admin/gifts/${id}`),
  createGift: (data) => api.post('/admin/gifts', data),
  updateGift: (id, data) => api.put(`/admin/gifts/${id}`, data),
  deleteGift: (id) => api.delete(`/admin/gifts/${id}`),
  activateGift: (id) => api.put(`/admin/gifts/${id}/activate`),
  deactivateGift: (id) => api.put(`/admin/gifts/${id}/deactivate`),
  getGiftStats: () => api.get('/admin/gifts/stats'),
  uploadGiftImage: (formData) => api.post('/admin/gifts/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const walletsAPI = {
  getWallets: (params) => api.get('/admin/wallets', { params }),
  getWallet: (userId) => api.get(`/admin/wallets/user/${userId}`),
  updateWalletBalance: (userId, data) => api.put(`/admin/wallets/${userId}/update-balance`, data),
  addCoins: (userId, data) => api.post(`/admin/wallets/${userId}/add-coins`, data),
  deductCoins: (userId, data) => api.post(`/admin/wallets/${userId}/deduct-coins`, data),
  getTransactions: (userId, params) => api.get(`/admin/wallets/${userId}/transactions`, { params }),
  getAllTransactions: (params) => api.get('/admin/wallets/transactions', { params }),
  getWalletStats: () => api.get('/admin/wallets/stats'),
  getTransactionStats: () => api.get('/admin/wallets/transactions/stats'),
  getExchangeSlabs: () => api.get('/users/exchange-slabs'),
  updateExchangeSlabs: (data) => api.put('/admin/wallets/exchange-slabs', data),
};

export const reportsAPI = {
  getDailyReport: (params) => api.get('/admin/reports/daily', { params }),
  getMonthlyReport: (params) => api.get('/admin/reports/monthly', { params }),
  getUserGrowthReport: (params) => api.get('/admin/reports/users', { params }),
  getRevenueReport: (params) => api.get('/admin/reports/revenue', { params }),
  getSessionReport: (params) => api.get('/admin/reports/sessions', { params }),
  getDashboardSummary: () => api.get('/admin/reports/dashboard-summary'),
};

export const agenciesAPI = {
  login: (identifier, password) => api.post('/agencies/login', { identifier, password }),
  verifyOtp: (agency_id, otp) => api.post('/agencies/verify-otp', { agency_id, otp }),
  getAgencies: (params) => api.get('/agencies', { params }),
  getAgency: (id) => api.get(`/agencies/${id}`),
  updateAgencyStatus: (id, status) => api.put(`/agencies/${id}/status`, { status }),
  deleteAgency: (id) => api.delete(`/agencies/${id}`),
  getDashboardStats: () => api.get('/agencies/dashboard/stats'),
  rechargeUser: (data) => api.post('/agencies/recharge', data),
  requestRechargeOTP: () => api.post('/agencies/recharge/request-otp'),
  getUsersList: (search) => api.get('/agencies/users/list', { params: { search } }),
  getRechargeHistory: () => api.get('/agencies/recharge/history'),
  topUpWallet: (amount) => api.post('/agencies/topup', { amount }),
  changePassword: (data) => api.put('/agencies/change-password', data),
  getHostApplications: () => api.get('/agencies/hosts/applications'),
  updateHostApplication: (id, status) => api.put(`/agencies/hosts/applications/${id}`, { status }),
  getAgencyHosts: (agencyId) => api.get(`/agencies/${agencyId}/hosts`),
};

export const momentsAPI = {
  getMoments: (params) => api.get('/admin/moments', { params }),
  getMoment: (id) => api.get(`/admin/moments/${id}`),
  updateMoment: (id, data) => api.put(`/admin/moments/${id}`, data),
  deleteMoment: (id) => api.delete(`/admin/moments/${id}`),
  getCategories: () => api.get('/admin/moments/categories'),
  updateStatus: (id, data) => api.put(`/admin/moments/${id}/status`, data),
};

export const roomsAPI = {
  getActiveRooms: () => api.get('/rooms'), // Gets active rooms
  getAllRooms: () => api.get('/rooms/all'), // Gets all rooms (Admin only)
  endRoom: (id) => api.delete(`/rooms/${id}`), // Force end room

};

export const storeAPI = {
  getItems: (category) => api.get('/store/items', { params: { category } }),
  createItem: (data) => api.post('/store', data),
  updateItem: (id, data) => api.put(`/store/${id}`, data),
  deleteItem: (id) => api.delete(`/store/${id}`),
};

export const feedbackAPI = {
  getFeedback: () => api.get('/feedback'),
  resolveFeedback: (id, status) => api.put(`/feedback/${id}/resolve`, { status }),
};

export const sellerAPI = {
  login: (identifier, password) => api.post('/seller/login', { identifier, password }),
  getProfile: () => api.get('/seller/me'),
  getDashboardStats: () => api.get('/seller/dashboard-stats'),
  searchUser: (q) => api.get('/seller/search-user', { params: { q } }),
  transferCoins: (data) => api.post('/seller/transfer-coins', data),
  getTransactions: (params) => api.get('/seller/transactions', { params }),
};

export default api;